/**
 * Pure derivation of `Recurrence` (the strict discriminated union) from the
 * loose BE wire fields on `MasterStopApi` / `DailyStopApi`. Lives in the
 * adapter layer because boundary validation is its job; `utils/route
 * StopOperations/recurrence.js` is a different consumer (display strings).
 * Today only `weekly` / `on-request` / `one-off` are reliably distinguished;
 * monthly + every-N-week land in `weekly` until BE shape confirms.
 */
import type { PricedServiceType } from 'types/entities/shared'
import type { DayOfWeek } from 'types/entities/shared/ids'
import type { Recurrence } from 'types/modules/routing/Recurrence'

const DAY_NAME_TO_LOWER: Readonly<Record<string, DayOfWeek>> = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday',
}

/** Single-letter (`UMTWRFS`) → lowercase day union, matches v1 conventions. */
const DAY_ABBR_TO_LOWER: Readonly<Record<string, DayOfWeek>> = {
  U: 'sunday',
  M: 'monday',
  T: 'tuesday',
  W: 'wednesday',
  R: 'thursday',
  F: 'friday',
  S: 'saturday',
}

/**
 * Parses the BE `daysOfWeek` CSV into a strict `DayOfWeek[]`.
 *
 * Tolerates the two formats observed in current BE responses:
 *   - Full names: `'MONDAY,WEDNESDAY'` (master rows).
 *   - Abbreviations: `'MWF'` (some legacy daily rows).
 *
 * Unknown tokens are dropped silently — they're a BE quirk, not a contract
 * violation. (`hasRecurrenceOnDate` in v1 also tolerates this.)
 */
export const parseBeDaysOfWeek = (csv: string | null | undefined): ReadonlyArray<DayOfWeek> => {
  if (!csv) return []

  if (csv.includes(',') || csv.length > 7) {
    return csv
      .split(',')
      .map(token => DAY_NAME_TO_LOWER[token.trim().toUpperCase()])
      .filter((d): d is DayOfWeek => d !== undefined)
  }

  const seen = new Set<DayOfWeek>()
  const out: DayOfWeek[] = []
  for (const ch of csv) {
    const day = DAY_ABBR_TO_LOWER[ch.toUpperCase()]
    if (day && !seen.has(day)) {
      seen.add(day)
      out.push(day)
    }
  }
  return out
}

/**
 * Inputs for `deriveRecurrence`. Loose by design — the BE quirks live here so
 * the adapter callers can hand off their raw fields verbatim.
 */
export interface DeriveRecurrenceInput {
  readonly daysOfWeek: string | null
  readonly recurrenceFrequency: string | null
  readonly recurrenceInterval: string | null
  readonly recurrencePer: string | null
  readonly pricedServiceType: PricedServiceType
  readonly configuredServiceEventType: string | null
}

const ONE_OFF_EVENT_TYPE = 'one off'

const isOneOffEventType = (eventType: string | null): boolean =>
  typeof eventType === 'string' && eventType.toLowerCase() === ONE_OFF_EVENT_TYPE

const parseFrequency = (raw: string | null): number => {
  if (!raw) return 1
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1) return 1
  return Math.floor(n)
}

const isWeeklyInterval = (interval: string | null): boolean => {
  if (!interval) return false
  const lower = interval.toLowerCase()
  return lower === 'week' || lower === 'weekly' || lower.includes('week')
}

/**
 * Derive a strict `Recurrence` from raw BE recurrence fields.
 *
 * Decision tree (matches the v1 semantics in
 * `utils/routeStopOperations/recurrence.js` and the master-board's
 * `getIsTemporaryEvent` rules):
 *
 *   1. `pricedServiceType === 'On Request'`  → `on-request`.
 *   2. `configuredServiceEventType === 'one off'` (case-insensitive) → `one-off`.
 *   3. Weekly interval + frequency 1 → `weekly`.
 *   4. Weekly interval + frequency 2 → `every-other-week` (parity unknown
 *      from the BE wire; default `'odd'` and let the caller override later).
 *   5. Weekly interval + frequency >= 2 → `every-N-week`.
 *   6. Anything else → `weekly` (best-effort default).
 *
 * No throws — this function is loose on purpose. The fields it interprets are
 * all FE-derived from string literals the BE has historically been
 * inconsistent about. A bad value falls back to `weekly` instead of
 * surfacing as an `AdapterError`; the caller owns the strict id/sequence
 * validation that *does* throw.
 *
 * Note: the `monthly-nth-weekday` variant requires BE fields not yet on
 * `MasterStopApi` — call sites read them from `eventActionType` payloads on
 * configured-service-event detail responses. Until that BE shape is pinned,
 * monthly patterns surface as `weekly` here (matches v1's display-string fallback).
 */
export const deriveRecurrence = (input: DeriveRecurrenceInput): Recurrence => {
  if (input.pricedServiceType === 'On Request') {
    return { kind: 'on-request' }
  }

  if (isOneOffEventType(input.configuredServiceEventType)) {
    return { kind: 'one-off' }
  }

  const days = parseBeDaysOfWeek(input.daysOfWeek)
  const frequency = parseFrequency(input.recurrenceFrequency)
  const weeklyInterval = isWeeklyInterval(input.recurrenceInterval) || isWeeklyInterval(input.recurrencePer)

  if (weeklyInterval || days.length > 0) {
    if (frequency === 1) {
      return { kind: 'weekly', daysOfWeek: days }
    }
    if (frequency === 2) {
      return { kind: 'every-other-week', daysOfWeek: days, weekParity: 'odd' }
    }
    return { kind: 'every-N-week', n: frequency, daysOfWeek: days, anchorDate: '' }
  }

  return { kind: 'weekly', daysOfWeek: days }
}
